// ABOUTME: Pure helpers that build and mutate the dashboard layout tree (sections -> rows -> columns, plus an
// ABOUTME: optional trailing tab body). Every node carries a stable uuid so dnd-kit can sort it.
import { v4 as uuidv4 } from "uuid";

// ----------------------
// Node factories — every node gets a stable id (dnd-kit sortable items require it).
// ----------------------
const newColumn = () => ({ id: uuidv4(), type: "column", width: 1, chart: {} });
const newRow = () => ({ id: uuidv4(), type: "row", totalWidth: 1, children: [newColumn()] });
const newSection = () => ({ id: uuidv4(), type: "section", children: [newRow()] });
// A tab's name is a display string the user can rename afterwards, so it is translated by the caller
// at creation time rather than resolved through t() on every render.
const newTab = (name) => ({ id: uuidv4(), name, children: [newSection()] });

export const reportBuilderBaseConfig = (config) => ({
    name: config.name,
    user: config.user,
    primary: false,
    dateRange: config.dateRange,
    filters: [],
    dashboard: {
        // Ordered page-level grey sections (the "main elements" of the page).
        sections: [newSection()],
        // Optional tabbed area, always rendered last. Null until the user adds it.
        tabBody: null,
    },
});

// Fresh empty layout for a brand-new dashboard: one page section, no tab body.
export const reportBuilderInitialDashboard = () => ({ sections: [newSection()], tabBody: null });

// ----------------------
// Tab body — optional trailing tabbed area
// ----------------------

// Add the tab body with one starter tab; sets it active. No-op if one already exists.
export const reportBuilderAddTabBody = (dashboard, tabName) => {
    if (dashboard.tabBody) return dashboard;
    const tab = newTab(tabName);
    return {
        ...dashboard,
        tabBody: {
            activeTabKey: tab.id,
            tabs: { [tab.id]: tab },
        },
    };
};

// Remove the whole tab body (and its tabs).
export const reportBuilderRemoveTabBody = (dashboard) => ({ ...dashboard, tabBody: null });

// ----------------------
// Tabs (inside the tab body)
// ----------------------

export const reportBuilderAddTab = (dashboard, tabName) => {
    if (!dashboard.tabBody) return dashboard;
    const tab = newTab(tabName);
    return {
        ...dashboard,
        tabBody: {
            ...dashboard.tabBody,
            tabs: { ...dashboard.tabBody.tabs, [tab.id]: tab },
        },
    };
};

export const reportBuilderRemoveTab = (dashboard, tabKeyToRemove, activeTabKey) => {
    // Do not allow removing the active tab.
    if (!dashboard.tabBody || tabKeyToRemove === activeTabKey) return dashboard;

    const { [tabKeyToRemove]: _removed, ...remainingTabs } = dashboard.tabBody.tabs;

    return {
        ...dashboard,
        tabBody: { ...dashboard.tabBody, tabs: remainingTabs },
    };
};

export const reportBuilderRenameTab = (dashboard, tabKey, newName) => {
    if (!dashboard.tabBody?.tabs?.[tabKey]) return dashboard;

    return {
        ...dashboard,
        tabBody: {
            ...dashboard.tabBody,
            tabs: {
                ...dashboard.tabBody.tabs,
                [tabKey]: { ...dashboard.tabBody.tabs[tabKey], name: newName },
            },
        },
    };
};

// ----------------------
// Sections — operate on a container array (dashboard.sections OR a tab's children)
// ----------------------

// Append a section to a container array. Returns a NEW array.
export const reportBuilderAddSection = (sections, section = null) => [...sections, section || newSection()];

// Remove a section by index from a container array. Returns a NEW array.
export const reportBuilderRemoveSection = (sections, sectionIndex) =>
    sections.filter((_, idx) => idx !== sectionIndex);

// Divide a section into two halves (main 3 / side 1). Only once.
export const reportBuilderDivideSection = (section) => {
    if (section.divided) return section; // cannot divide twice

    return {
        ...section,
        divided: true,
        children: [
            { id: uuidv4(), type: "divided_section", width: 3, children: [...section.children] }, // original content
            { id: uuidv4(), type: "divided_section", width: 1, children: [newRow()] }, // empty half ready for rows
        ],
    };
};

// Remove one divided half. If only one remains, flatten its rows back into the section.
export const reportBuilderRemoveDividedSection = (section, dividerIndex) => {
    if (!section.divided) return section;

    const newChildren = section.children.filter((_, idx) => idx !== dividerIndex);

    if (newChildren.length === 1) {
        return {
            ...section,
            divided: false,
            children: [...newChildren[0].children], // flatten rows back into section
        };
    }

    return { ...section, children: newChildren };
};

// Resize a divided half — clamp 1..5 and set that half's flex width (mirrors reportBuilderResizeColumn).
export const reportBuilderResizeDividedSection = (section, dividerIndex, newWidth) => {
    if (!section.divided) return section;
    const clampedWidth = Math.min(5, Math.max(1, newWidth));

    return {
        ...section,
        children: section.children.map((divider, idx) =>
            idx === dividerIndex ? { ...divider, width: clampedWidth } : divider
        ),
    };
};

// ----------------------
// Rows & Columns
// ----------------------

// Add a row to a section (or a specific divided half).
export const reportBuilderAddRow = (section, dividerIndex = null) => {
    if (section.divided && dividerIndex !== null) {
        return {
            ...section,
            children: section.children.map((divider, idx) =>
                idx === dividerIndex
                    ? { ...divider, children: [...divider.children, newRow()] }
                    : divider
            ),
        };
    }

    return { ...section, children: [...section.children, newRow()] };
};

// Remove a row from a section (or a specific divided half).
export const reportBuilderRemoveRow = (section, rowIndex, dividerIndex = null) => {
    if (section.divided && dividerIndex !== null) {
        return {
            ...section,
            children: section.children.map((divider, idx) =>
                idx === dividerIndex
                    ? { ...divider, children: divider.children.filter((_, rIdx) => rIdx !== rowIndex) }
                    : divider
            ),
        };
    }

    return { ...section, children: section.children.filter((_, rIdx) => rIdx !== rowIndex) };
};

// Remove a column from a row.
export const reportBuilderRemoveColumn = (section, rowIndex, colIndex, dividerIndex = null) => {
    const stripColumn = (row) => ({
        ...row,
        children: row.children.filter((_, cIdx) => cIdx !== colIndex),
        totalWidth: row.children
            .filter((_, cIdx) => cIdx !== colIndex)
            .reduce((sum, c) => sum + (c.width || 1), 0),
    });

    if (section.divided && dividerIndex !== null) {
        return {
            ...section,
            children: section.children.map((divider, idx) =>
                idx === dividerIndex
                    ? { ...divider, children: divider.children.map((row, rIdx) => (rIdx === rowIndex ? stripColumn(row) : row)) }
                    : divider
            ),
        };
    }

    return { ...section, children: section.children.map((row, rIdx) => (rIdx === rowIndex ? stripColumn(row) : row)) };
};

// Add a column to a row.
export const reportBuilderAddColumn = (section, rowIndex, dividerIndex = null) => {
    const appendColumn = (row) => ({
        ...row,
        children: [...row.children, newColumn()],
        totalWidth: row.children.reduce((sum, c) => sum + c.width, 0) + 1,
    });

    if (section.divided && dividerIndex !== null) {
        return {
            ...section,
            children: section.children.map((divider, idx) =>
                idx === dividerIndex
                    ? { ...divider, children: divider.children.map((row, rIdx) => (rIdx === rowIndex ? appendColumn(row) : row)) }
                    : divider
            ),
        };
    }

    return { ...section, children: section.children.map((row, rIdx) => (rIdx === rowIndex ? appendColumn(row) : row)) };
};

export const reportBuilderResizeColumn = (section, rowIndex, colIndex, newWidth, dividerIndex = null) => {
    const clampedWidth = Math.min(4, Math.max(1, newWidth)); // Clamp between 1 and 4

    const resize = (row) => ({
        ...row,
        children: row.children.map((col, cIdx) => (cIdx === colIndex ? { ...col, width: clampedWidth } : col)),
        totalWidth: row.children.reduce((sum, c, cIdx) => sum + (cIdx === colIndex ? clampedWidth : c.width), 0),
    });

    if (section.divided && dividerIndex !== null) {
        return {
            ...section,
            children: section.children.map((divider, idx) =>
                idx === dividerIndex
                    ? { ...divider, children: divider.children.map((row, rIdx) => (rIdx === rowIndex ? resize(row) : row)) }
                    : divider
            ),
        };
    }

    return { ...section, children: section.children.map((row, rIdx) => (rIdx === rowIndex ? resize(row) : row)) };
};

export const reportBuilderSetPrimary = (dashboard) => ({ ...dashboard, primary: true });
